"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { ContactSalesProvider } from "@/components/ContactSalesModal/ContactSalesProvider";
import { NewVersionToast } from "@/components/NewVersionToast/NewVersionToast";
import { ChameleonProvider } from "./ChameleonProvider";
import { EmsEntitlementGate } from "./EmsEntitlementGate/EmsEntitlementGate";
import { EmsQuotaErrorListener } from "./EmsQuotaErrorListener/EmsQuotaErrorListener";
import { ExplainProvider } from "./ExplainProvider/ExplainProvider";
import { FullstoryProvider } from "./FullstoryProvider";
import { LaunchDarklyClientWrapper } from "./LaunchDarklyProvider/LaunchDarklyClientWrapper";
import { LaunchDarklyContextUpdater } from "./LaunchDarklyProvider/LaunchDarklyContextUpdater";
import { NotificationPermissionPrompt } from "./NotificationPermissionPrompt";
import { NotificationsProvider } from "./NotificationsProvider";
import { OrganizationProvider } from "./OrganizationProvider";
import { PushSubscriber } from "./PushSubscriber";
import { PylonContextProvider, PylonProvider } from "./PylonProvider";
import { RoleChangeSubscriber } from "./RoleChangeSubscriber/RoleChangeSubscriber";
import { RoleMembershipSubscriber } from "./RoleMembershipSubscriber/RoleMembershipSubscriber";
import { RollbarUserContext } from "./RollbarProvider/RollbarUserContext";
import { TeamsProvider } from "./TeamsProvider";
import { ThreadNamedSubscriber } from "./ThreadNamedSubscriber/ThreadNamedSubscriber";
import { TrialErrorListener } from "./TrialErrorListener/TrialErrorListener";
import { UserChannelProvider } from "./UserChannelProvider";
import { UserChannelSubscriber } from "./UserChannelSubscriber";
const AblyProvider = dynamic(() => import("./AblyProvider/AblyProvider").then(mod => mod.AblyProvider), {
  ssr: false
});
const UploadProvider = dynamic(() => import("./UploadProvider").then(mod => mod.UploadProvider), {
  ssr: false
});
interface AuthenticatedProvidersProps {
  children: ReactNode;
  orgId?: string;
  teamId?: string;
}

/**
 * AuthenticatedProviders - Providers requiring authenticated user
 *
 * These providers depend on user authentication and will fail
 * on public pages. They must only be used in authenticated route groups.
 *
 * CRITICAL: Provider order matters! Do not reorder without understanding
 * the dependency chain:
 *
 * 1. PylonContextProvider (manages Pylon show/hide state, no dependencies)
 * 2. OrganizationProvider (uses API, no useMe dependency)
 * 3. PylonProvider (uses useMe, configures Pylon user settings)
 * 4. FullstoryProvider (uses useMe)
 * 5. RollbarUserContext (uses useMe, sets Rollbar user context)
 * 5a. NewVersionToast (polls /api/version; needs RollbarProvider from the root layout and the ToastProvider)
 * 6. AblyProvider (uses useMe, connects realtime)
 * 7. TeamsProvider (uses OrganizationProvider + useMe)
 * 8. EmsEntitlementGate (uses OrganizationProvider's currentEntitlement + useAbility/useMe)
 * 9. LaunchDarklyContextUpdater (uses useMe + TeamsProvider)
 * 10. ChameleonProvider (uses useMe + TeamsProvider + OrganizationProvider)
 * 11. RoleMembershipSubscriber (uses AblyProvider + TeamsProvider; keeps role-derived reads live)
 * 12. UserChannelProvider (uses useMe)
 * 13. NotificationsProvider (uses authenticated API)
 * 14. RoleChangeSubscriber (depends on UserChannelProvider + TeamsProvider)
 * 15. UserChannelSubscriber (depends on UserChannelProvider)
 * 16. NotificationPermissionPrompt (gates push activation behind permission)
 * 17. PushSubscriber (sibling of UserChannelSubscriber, renders after push activated)
 * 18. UploadProvider (uses useActiveHierarchyScope -> TeamsProvider)
 * 19. ContactSalesProvider (used by TrialErrorModal + TrialBanner; depends on OrganizationProvider + useMe)
 * 20. TrialErrorListener (subscribes to MutationCache; its modal calls useContactSales so it must sit inside the provider)
 * 21. EmsQuotaErrorListener (subscribes to MutationCach)
 * 22. ThreadNamedSubscriber (depends on AblyProvider + UserChannelProvider)
 */
export function AuthenticatedProviders(t0) {
  const $ = _c(19);
  const {
    children,
    orgId,
    teamId
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PylonProvider />;
    t2 = <FullstoryProvider />;
    t3 = <RollbarUserContext />;
    t4 = <NewVersionToast />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  let t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <LaunchDarklyContextUpdater />;
    t6 = <ChameleonProvider />;
    t7 = <RoleMembershipSubscriber />;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== children) {
    t8 = <RoleChangeSubscriber><UserChannelSubscriber><ExplainProvider><UploadProvider><ContactSalesProvider><TrialErrorListener><EmsQuotaErrorListener>{children}</EmsQuotaErrorListener></TrialErrorListener></ContactSalesProvider></UploadProvider></ExplainProvider></UserChannelSubscriber></RoleChangeSubscriber>;
    $[7] = children;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ThreadNamedSubscriber />;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <NotificationPermissionPrompt><PushSubscriber /></NotificationPermissionPrompt>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t8) {
    t11 = <EmsEntitlementGate>{t5}{t6}{t7}<UserChannelProvider><NotificationsProvider>{t8}{t9}{t10}</NotificationsProvider></UserChannelProvider></EmsEntitlementGate>;
    $[11] = t8;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11 || $[14] !== teamId) {
    t12 = <AblyProvider><TeamsProvider teamId={teamId}>{t11}</TeamsProvider></AblyProvider>;
    $[13] = t11;
    $[14] = teamId;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== orgId || $[17] !== t12) {
    t13 = <LaunchDarklyClientWrapper><PylonContextProvider><OrganizationProvider orgId={orgId}>{t1}{t2}{t3}{t4}{t12}</OrganizationProvider></PylonContextProvider></LaunchDarklyClientWrapper>;
    $[16] = orgId;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  return t13;
}